import type { JiraSprintData } from '../../../api/endpoints/jira';
import { memoAll } from '../../../CodesForFoundation';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { Attribute, ComponentJson, ObjectManager, component, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import { Physical, PotentialInteraction, RelativeTo, TransformHints } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { UIField } from '../../canvas/entities/UIField';
import { GraffitiTarget } from '../../canvas/visualization/Graffiti';
import { AutoLayoutSubject } from '../autoLayout/AutoLayout';
import { ConnectTarget } from '../connector/ConnectTarget';
import { Pingable } from '../presentation/Pingable';
import { AtlassianContext } from './AtlassianContext';
import { JiraSprintFromDateField, JiraSprintToDateField } from './fields/JiraSprintDateField';
import { JiraSprintGoalField } from './fields/JiraSprintGoalField';
import { JiraSprintNameField } from './fields/JiraSprintNameField';
import { JiraSprintLayout } from './JiraSprintLayout';

const invocation = defineCommand(
    'jiraSprintInvoke',
    null,
    (cc, [id, hint]: [string, string?]) => {
        // Basic invocation handler
    },
    [],
);

const mutation = defineCommand(
    'jiraSprintMutate',
    null,
    (cc, [id]: [string]) => {
        const objects = cc.resolve(ObjectManager);
        const sprint = objects.getMaybe(id, JiraSprint);
        if (!sprint) return;
        cc.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const commit = defineCommand(
    'jiraSprintCommit',
    null,
    (cc, []: [string]) => {
        cc.resolve(AtlassianContext).flushIssuePatches();
    },
    [],
);

const jiraSprintDef = {
    name: 'JiraSprint',
    implements: [
        Physical,
        AutoBringToFront,
        AutoLayoutSubject,
        ConnectTarget,
        Pingable,
        GraffitiTarget
    ],
    invocation,
    mutation,
    commit,
};

export const JiraSprintDataRecordAttr = new Attribute<JiraSprintData, JiraSprint>('jiraDataRecord', null);

@component(jiraSprintDef)
export class JiraSprint extends RectangularPhysical {

    @property({ type: 'number', default: 360 })
    public width!: number;

    @property({ type: 'number', default: 160 })
    public height!: number;

    @property({ type: 'string' })
    public jiraSprintId!: string;

    public get minWidth(): number {
        return 360;
    }

    public get minHeight(): number {
        return Math.max(160, this.layout.base.height);
    }

    @memoUsing(JiraSprintDataRecordAttr)
    public get record(): JiraSprintData {
        return JiraSprintDataRecordAttr.get(this);
    }

    @memoUsing('width', 'height', 'record')
    public get layout() {
        return JiraSprintLayout(this);
    }

    public get boundary(): boolean {
        return true;
    }

    @memoUsing('record', 'layout')
    public get uiFields(): readonly UIField[] {
        if (!!this.jiraSprintId) {
            return [
                JiraSprintNameField(this),
                JiraSprintFromDateField(this),
                JiraSprintToDateField(this),
                JiraSprintGoalField(this),
            ];
        }
        return [];
    }

    public area(to: RelativeTo | Physical = RelativeTo.Parent): Rect {
        return Rect.fromPoints(this.transform(to).apply(this.localBounds));
    }

    public testInteraction({ type, mpt }: PotentialInteraction, _force?: boolean, _selected?: boolean): [boolean, string] {
        const { base } = this.layout;
        const rpt = this.toRelative(mpt);
        if (base.contains(rpt)) {
            return [type !== 'invoke', 'default'];
        }
        return [false, 'none'];
    }

    @memoAll()
    public queryTransformHints(singular: boolean) {
        return TransformHints({
            scaleX: !!singular,
            scaleY: false,
            rotate: false,
            translate: true,
            visible: true,
        });
    }

    // public applyTransformation(scope: TransformScope, from: Figure, to: Figure): this {
    //     const tlt = this.modelLocalTransform();
    //     from = tlt.apply(from);
    //     to = tlt.apply(to);

    //     const [a, b, c] = scope.type === 'scale' ? this.jiggleForPotentialMirror(from, to) : to;

    //     const px = from[0].x;
    //     const pw = Point.vector(from[0], from[1]).length();
    //     const ph = Point.vector(from[1], from[2]).length();

    //     const wv = Point.vector(a, b);
    //     const hv = Point.vector(b, c);

    //     const nc = Rect.fromPoints(to).center();

    //     if (scope.type === 'scale' && scope.items.length === 1) {
    //         const x = px + (a.x - px);
    //         const w = Math.max(wv.length(), this.minWidth);

    //         if (this.width !== w) {
    //             this.setTransformationResult(
    //                 round(x, 4),
    //                 round(nc.y - (ph / 2), 4),
    //                 0,
    //                 round(w, 4),
    //                 round(this.minHeight, 4),
    //             );
    //         }
    //     } else {
    //         this.setTransformationResult(
    //             round(nc.x - (pw / 2), 4),
    //             round(nc.y - (ph / 2), 4),
    //             round(0, 4),
    //             round(pw, 4),
    //             round(this.minHeight, 4),
    //         );
    //     }

    //     return this;
    // }

    public sizeToContent() {
        if (this.height !== this.minHeight) {
            this.height = this.minHeight;
        }
    }

    public toJson(to?: RelativeTo | Physical): ComponentJson {
        const base = super.toJson(to);
        const data = JiraSprintDataRecordAttr.get(this);
        return {
            ...base,
            jiraSprintId: this.jiraSprintId,
            name: data?.name,
            startDate: data?.startDate,
            endDate: data?.endDate,
            goal: data?.goal,
        };
    }

    public toHumanString(): string {
        const data = JiraSprintDataRecordAttr.get(this);
        return `JiraSprint ("${(data?.name || this.jiraSprintId).slice(0, 20)}")`;
    }
}
